
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import {
    ArrowRight,
    ChevronDown,
    ChevronRight,
    CircleHelp,
    CreditCard,
    Headphones,
    Hotel,
    Mail,
    MessageCircle,
    ShieldCheck,
    Sparkles,
} from "lucide-react";

import Hero from "../components/Hero";
import hotelImg from "../assests/images/hotel-bg.jpg";

const faqCategories = [
    {
        title: "Hotel Bookings",
        icon: Hotel,
        faqs: [
            {
                question: "How do I book a hotel?",
                answer:
                    "You can browse hotels on our website, select your preferred option, and complete the booking with instant confirmation.",
            },
            {
                question: "Can I cancel my booking?",
                answer:
                    "Yes, most bookings are refundable. Cancellation policy depends on the hotel or service provider.",
            },
        ],
    },
    {
        title: "Travel Services",
        icon: Sparkles,
        faqs: [
            {
                question: "How do I get visa assistance?",
                answer:
                    "You can apply through our visa services section. Our team will guide you through the entire process.",
            },
            {
                question: "Do you offer flight booking support?",
                answer:
                    "Yes, we provide affordable flight booking options worldwide with flexible dates and airlines.",
            },
        ],
    },
    {
        title: "Payments & Support",
        icon: CreditCard,
        faqs: [
            {
                question: "Is my payment secure?",
                answer:
                    "Yes, all payments are encrypted and processed through secure gateways to ensure full protection.",
            },
            {
                question: "Do you provide 24/7 customer support?",
                answer:
                    "Absolutely! Our support team is available 24/7 to assist you with any travel-related queries.",
            },
        ],
    },
];

export default function Faqs() {
    const [activeIndex, setActiveIndex] = useState("0-0");

    const toggleFAQ = (id) => {
        setActiveIndex((current) => (current === id ? null : id));
    };

    const siteUrl =
        typeof window !== "undefined"
            ? window.location.origin
            : "";

    return (
        <>
            {/* SEO */}
            <Helmet>
                <title>
                    FAQs | Dubai Budgetive Hotels - Frequently Asked Questions
                </title>

                <meta
                    name="description"
                    content="Find answers to frequently asked questions about Dubai hotel bookings, cancellations, payments, refunds, and travel services at Dubai Budgetive Hotels."
                />

                <link
                    rel="canonical"
                    href={`${siteUrl}/faqs`}
                />

                <meta
                    property="og:title"
                    content="FAQs | Dubai Budgetive Hotels"
                />

                <meta
                    property="og:description"
                    content="Find answers to frequently asked questions about booking hotels in Dubai, payments, cancellations, and travel services."
                />

                <meta property="og:type" content="website" />
                <meta property="og:url" content={`${siteUrl}/faqs`} />

                <meta
                    name="twitter:card"
                    content="summary"
                />

                <meta
                    name="twitter:title"
                    content="FAQs | Dubai Budgetive Hotels"
                />

                <meta name="robots" content="index, follow" />
            </Helmet>

            <main className="min-h-screen bg-[#F5FAF7] text-[#17324D]">
                {/* HERO */}
                <div className="relative">
                    <Hero
                        variant="inner"
                        title="Frequently Asked Questions"
                        subtitle="Everything you need to know about your stay and booking experience."
                        backgroundImage={hotelImg}
                    />
                </div>

                {/* INTRO */}
                <section className="relative overflow-hidden bg-[#F5FAF7] px-4 pb-12 pt-16 sm:px-6 lg:px-8">
                    <div className="pointer-events-none absolute -right-28 top-0 h-80 w-80 rounded-full bg-[#DDF5E6] opacity-70 blur-3xl" />

                    <div className="relative mx-auto max-w-[1536px] text-center">
                        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#CBEAD6] bg-white px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.13em] text-[#168B43]">
                            <CircleHelp size={16} />
                            Help Center
                        </div>

                        <h1 className="mx-auto mt-6 max-w-[750px] text-[32px] font-extrabold leading-tight tracking-tight text-[#17324D] sm:text-[42px]">
                            Have Questions?{" "}
                            <span className="text-[#168B43]">
                                We Have Answers.
                            </span>
                        </h1>

                        <p className="mx-auto mt-5 max-w-[620px] text-[14px] leading-8 text-[#718096]">
                            Explore our most frequently asked questions about
                            hotel reservations, travel services, payments, and
                            customer support.
                        </p>
                    </div>
                </section>

                {/* FAQ CONTENT */}
                <section className="px-4 pb-20 sm:px-6 lg:px-8">
                    <div className="mx-auto grid max-w-[1536px] items-start gap-8 lg:grid-cols-[300px_minmax(0,1fr)]">
                        {/* SIDEBAR */}
                        <aside className="space-y-5 lg:sticky lg:top-28">
                            <div className="rounded-[24px] border border-[#DCEAE1] bg-white p-6 shadow-[0_12px_40px_rgba(6,78,59,0.04)]">
                                <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                                    Browse Topics
                                </p>

                                <h2 className="mt-2 text-[21px] font-extrabold text-[#17324D]">
                                    FAQ Categories
                                </h2>

                                <div className="mt-6 space-y-2">
                                    {faqCategories.map((category, index) => {
                                        const Icon = category.icon;

                                        return (
                                            <a
                                                key={category.title}
                                                href={`#faq-category-${index}`}
                                                className="group flex items-center justify-between gap-3 rounded-xl border border-transparent px-4 py-3.5 transition hover:border-[#D2EAD9] hover:bg-[#F0FAF3]"
                                            >
                                                <span className="flex items-center gap-3 text-[13px] font-bold text-[#17324D] group-hover:text-[#168B43]">
                                                    <Icon
                                                        size={18}
                                                        className="text-[#168B43]"
                                                    />
                                                    {category.title}
                                                </span>

                                                <ChevronRight
                                                    size={16}
                                                    className="text-[#94A3B8] group-hover:text-[#168B43]"
                                                />
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* SIDEBAR HELP CARD */}
                            <div className="relative overflow-hidden rounded-[24px] bg-[#064E3B] p-7 text-white">
                                <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#168B43]/40 blur-2xl" />

                                <div className="relative">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-[#A5E8B8]">
                                        <Headphones size={24} />
                                    </div>

                                    <h3 className="mt-6 text-[22px] font-extrabold text-white">
                                        Need More Help?
                                    </h3>

                                    <p className="mt-3 text-[13px] leading-7 text-white/70">
                                        Can't find the answer you're looking for?
                                        Our team is here to help you.
                                    </p>

                                    <Link
                                        to="/contact-us"
                                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-5 py-3.5 text-[12px] font-extrabold text-white transition hover:bg-[#21A253]"
                                    >
                                        Contact Support
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </div>
                        </aside>

                        {/* FAQ LIST */}
                        <div className="space-y-7">
                            {faqCategories.map((category, categoryIndex) => {
                                const CategoryIcon = category.icon;

                                return (
                                    <section
                                        key={category.title}
                                        id={`faq-category-${categoryIndex}`}
                                        className="scroll-mt-28 overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white shadow-[0_12px_40px_rgba(6,78,59,0.04)]"
                                    >
                                        {/* CATEGORY HEADER */}
                                        <div className="flex items-center gap-4 border-b border-[#E8F0EA] px-6 py-6 sm:px-8">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                                <CategoryIcon size={23} />
                                            </div>

                                            <div>
                                                <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#168B43]">
                                                    Frequently Asked Questions
                                                </p>

                                                <h2 className="mt-1 text-[21px] font-extrabold text-[#17324D]">
                                                    {category.title}
                                                </h2>
                                            </div>
                                        </div>

                                        {/* QUESTIONS */}
                                        <div className="divide-y divide-[#E8F0EA] px-5 sm:px-8">
                                            {category.faqs.map((item, index) => {
                                                const id = `${categoryIndex}-${index}`;
                                                const isOpen = activeIndex === id;

                                                return (
                                                    <div key={id} className="py-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleFAQ(id)}
                                                            aria-expanded={isOpen}
                                                            aria-controls={`faq-answer-${id}`}
                                                            className="flex w-full items-center justify-between gap-5 py-6 text-left"
                                                        >
                                                            <span className="flex items-start gap-4">
                                                                <span
                                                                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-extrabold ${isOpen
                                                                            ? "bg-[#168B43] text-white"
                                                                            : "bg-[#E8F7EE] text-[#168B43]"
                                                                        }`}
                                                                >
                                                                    {String(index + 1).padStart(2, "0")}
                                                                </span>

                                                                <span
                                                                    className={`text-[14px] font-extrabold leading-7 transition sm:text-[16px] ${isOpen
                                                                            ? "text-[#168B43]"
                                                                            : "text-[#17324D]"
                                                                        }`}
                                                                >
                                                                    {item.question}
                                                                </span>
                                                            </span>

                                                            <span
                                                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition ${isOpen
                                                                        ? "rotate-180 bg-[#E8F7EE] text-[#168B43]"
                                                                        : "bg-[#F5FAF7] text-[#718096]"
                                                                    }`}
                                                            >
                                                                <ChevronDown size={18} />
                                                            </span>
                                                        </button>

                                                        <div
                                                            id={`faq-answer-${id}`}
                                                            hidden={!isOpen}
                                                            className="pb-6 pl-11 pr-5 sm:pl-11"
                                                        >
                                                            <p className="max-w-[760px] text-[13px] leading-8 text-[#718096] sm:text-[14px]">
                                                                {item.answer}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* BOTTOM CTA */}
                <section className="border-t border-[#DCEAE1] bg-white px-4 py-16 sm:px-6 lg:px-8">
                    <div className="mx-auto flex max-w-[1536px] flex-col items-center justify-between gap-8 rounded-[28px] bg-[#EAF8EE] p-8 text-center sm:p-12 lg:flex-row lg:text-left">
                        <div className="flex max-w-[680px] flex-col items-center gap-5 lg:flex-row lg:items-start">
                            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white text-[#168B43]">
                                <MessageCircle size={29} />
                            </div>

                            <div>
                                <h2 className="text-[25px] font-extrabold tracking-tight text-[#064E3B] sm:text-[30px]">
                                    Still Have Questions?
                                </h2>

                                <p className="mt-3 text-[13px] leading-7 text-[#47715A]">
                                    Our support team is ready to assist with
                                    your hotel booking and travel questions.
                                </p>
                            </div>
                        </div>

                        <Link
                            to="/contact-us"
                            className="inline-flex shrink-0 items-center justify-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-[13px] font-extrabold text-white transition hover:bg-[#087D37]"
                        >
                            Get In Touch
                            <ArrowRight size={18} />
                        </Link>
                    </div>
                </section>
            </main>
        </>
    );
}
